import { useEffect, useLayoutEffect, useState } from 'react';
import { useUIStore } from '../store/uiStore';

/**
 * Hook to apply theme class to document root.
 * Should be called once at the app root level.
 *
 * Handles Zustand persist hydration properly by:
 * 1. Checking localStorage directly on mount for immediate theme application
 * 2. Subscribing to store changes for future updates
 */
export function useTheme() {
  const theme = useUIStore((state) => state.theme);
  const [hasHydrated, setHasHydrated] = useState(false);

  // Apply theme immediately on mount by reading localStorage directly
  // This prevents flash of wrong theme before Zustand hydrates
  useLayoutEffect(() => {
    const root = document.documentElement;

    // Read directly from localStorage to get persisted value immediately
    try {
      const stored = localStorage.getItem('ui-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        const storedTheme = parsed?.state?.theme;
        if (storedTheme === 'dark') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    } catch {
      // If localStorage read fails, fall back to store state
      if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }

    setHasHydrated(true);
  }, []);

  // After hydration, react to theme changes from the store
  useEffect(() => {
    if (!hasHydrated) return;

    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, hasHydrated]);

  // Also subscribe to store changes to catch any direct store updates
  useEffect(() => {
    const unsubscribe = useUIStore.subscribe((state) => {
      const root = document.documentElement;
      if (state.theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    });
    return unsubscribe;
  }, []);

  return theme;
}
